import { ChangeDetectionStrategy, Component, computed, input, output } from '@angular/core';
import { Profile } from 'src/app/core/schema/Profile.model';
import { AvatarComponent } from 'src/app/ui-kit/avatar/avatar.component';
import { CardComponent } from 'src/app/ui-kit/card/card/card.component';
import { ChipComponent } from 'src/app/ui-kit/chip/chip.component';
import { IconComponent } from 'src/app/ui-kit/icon/icon.component';

/**
 * Canonical card for a team {@link Profile} (staff member). Displays the staff member's
 * email, role, caseload size, and active status. Used in team admin views where admins
 * manage other staff members.
 *
 * @security FERPA — `email` and `notes` are PII. Never log or transmit.
 *
 * @feature shared
 */
@Component({
  selector: 'app-staff-member-card',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [AvatarComponent, CardComponent, ChipComponent, IconComponent],
  host: { class: 'block' },
  template: `
    <app-card
      [button]="clickable()"
      [ariaLabel]="'Open ' + displayName() + ' staff profile'"
      (click)="onClick()"
    >
      <div class="flex items-start gap-3">
        <app-avatar [name]="displayName()" size="lg" />

        <div class="min-w-0 flex-1">
          <div class="flex items-center gap-2">
            <h3 class="truncate text-base font-semibold text-body">{{ displayName() }}</h3>
            <app-chip [color]="activeChipColor()" size="xs">
              {{ activeChipLabel() }}
            </app-chip>
          </div>

          <p class="truncate text-sm text-muted">{{ profile().email }}</p>

          @if (profile().role) {
            <div class="mt-2 flex items-center gap-2">
              <app-icon name="shield-checkmark" size="xxs" color="medium" />
              <span class="text-xs font-semibold text-muted">{{ profile().role }}</span>
            </div>
          }
        </div>

        @if (clickable()) {
          <app-icon name="chevron-forward" size="xs" color="medium" />
        }
      </div>

      @if (caseloadSize() > 0) {
        <div class="mt-3 flex items-center gap-2 border-t border-outline-low pt-3">
          <app-icon name="people" size="xs" color="medium" />
          <span class="text-sm text-muted">{{ caseloadLabel() }}</span>
        </div>
      }
    </app-card>
  `,
})
export class StaffMemberCardComponent {
  /** The staff member profile. @security FERPA. */
  readonly profile = input.required<Profile>();

  /**
   * Optional display name override. Profiles store only an email — the caller often has
   * a joined User record with a first/last name. Pass it here to render a friendly name.
   */
  readonly nameOverride = input<string | undefined>(undefined);

  /** Whether the card is interactive. @default true */
  readonly clickable = input<boolean>(true);

  /** Fires when the card is clicked. */
  readonly cardClick = output<Profile>();

  /** Display name — override, then email. */
  readonly displayName = computed(() => this.nameOverride() || this.profile().email || 'Unknown');

  readonly caseloadSize = computed(() => this.profile().caseload?.length ?? 0);

  readonly caseloadLabel = computed(() => {
    const n = this.caseloadSize();
    return `${n} student${n === 1 ? '' : 's'} in caseload`;
  });

  readonly activeChipColor = computed<'success' | 'danger'>(() =>
    this.profile().isActive === false ? 'danger' : 'success',
  );

  readonly activeChipLabel = computed(() => (this.profile().isActive === false ? 'Inactive' : 'Active'));

  onClick(): void {
    if (this.clickable()) {
      this.cardClick.emit(this.profile());
    }
  }
}
